import React, { ChangeEvent, useMemo, useState } from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import { useModalFocus } from "./useModalFocus";
import "./MatchGridModal.scss";

export interface MatchGridColumn {
    readonly id: string;
    readonly label: string;
    /** Sortable header (master-demo-v6: FIRST NAME for patient, ORGANIZATION for requestor). */
    readonly sortable?: boolean;
    /**
     * AB#37980: "date" makes THIS column's filter compare dates rather than text, so the digits a
     * specialist types match the date the cell renders. Opt-in per column; never alters the box.
     */
    readonly filterKind?: "date";
}

export interface MatchGridRow {
    readonly id: string | number;
    /** Display + filter + sort text, keyed by column id. */
    readonly values: Readonly<Record<string, string>>;
    /** Optional inline nodes rendered after a cell's text (e.g. e-transfer/national badge icons on the requestor ORGANIZATION column). Not filtered/sorted on. */
    readonly adornments?: Readonly<Record<string, React.ReactNode>>;
}

export interface MatchGridModalProps {
    readonly title: string;
    readonly subtitle?: string;
    readonly columns: readonly MatchGridColumn[];
    readonly rows: readonly MatchGridRow[];
    /** A candidate row was clicked (AC9 - select/disambiguate). */
    readonly onSelect: (id: string | number) => void;
    /** Footer create-new label, e.g. "+ Create New Patient" / "+ Create New Requestor". */
    readonly createNewLabel: string;
    /**
     * "Create New" (AC9) - signals create-new on submit; the modal itself creates nothing.
     *
     * AB#38824: whether the create form that opens is PRE-FILLED with the AI-extracted values is
     * the row's call, not this modal's - it carries them forward only when no DB match was found
     * (createPrefill.ts). This doc used to promise the retention unconditionally, which stopped
     * being true the moment "found a match, adding someone else anyway" started opening empty.
     */
    readonly onCreateNew: () => void;
    readonly onClose: () => void;
}

type SortDirection = "asc" | "desc";

/** Same format hint the entry DOB field carries (InlineSearchForm) - AB#37980 asked for it here too. */
const DATE_FILTER_PLACEHOLDER = "mm/dd/yyyy";

/** Digits and the separators a date is typed with. A value with anything else is not a date. */
const DATE_FILTER_CHARS = /^[\d\s./,-]*$/;

const digitsOnly = (text: string): string => text.replace(/\D/g, "");

/** The separator-delimited runs of digits a filter was typed as: "1/2/70" -> ["1", "2", "70"]. */
const dateParts = (filter: string): string[] => filter.split(/\D+/).filter(part => part !== "");

/** MM | DD | YYYY of the zero-padded MM/DD/YYYY a DOB cell renders (toDisplayDob), or null. */
const cellDateParts = (cellText: string): readonly string[] | null => {
    const digits = digitsOnly(cellText);
    return digits.length === 8 ? [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)] : null;
};

/**
 * One typed part against the cell part it is aligned with. A part a separator has already closed is
 * compared whole - zero-padded, so "1/2/1970" answers 01/02/1970, and a 2-digit year in either
 * century. The LAST part is still being typed, so it ALSO matches as a prefix of its cell part:
 * "10/22/19" is the state every 4-digit year passes through, and treating that 19 as a finished
 * year alone would drop the 10/22/1996 patient it is being typed for, between keystrokes.
 */
const partMatches = (cellPart: string, part: string, isLast: boolean): boolean => {
    if (part.length > cellPart.length) {
        return false;
    }
    if (part.length === cellPart.length) {
        return part === cellPart;
    }
    if (cellPart.length === 4 && part.length === 2 && (cellPart === `19${part}` || cellPart === `20${part}`)) {
        return true;
    }
    if (cellPart.length === 2 && part.length === 1 && cellPart === `0${part}`) {
        return true;
    }
    return isLast && cellPart.startsWith(part);
};

/**
 * The date a filter's parts name, compared against the cell PART TO PART. A filter that carries
 * separators has said where each of its parts begins, so its parts are aligned with whole cell parts
 * and never slid across a boundary: "10/22" names a month and a day, so it must not answer
 * 01/02/2000, whose digit run merely contains 1022 - nor may it be read as a year, so "19/96"
 * (no such month) answers nothing rather than every patient born in 1996.
 */
const alignedPartsMatch = (cellText: string, parts: readonly string[]): boolean => {
    const cellParts = cellDateParts(cellText);
    if (cellParts === null || parts.length > cellParts.length) {
        return false;
    }
    const lastIndex = parts.length - 1;
    return cellParts.some(
        (_, offset) =>
            offset + parts.length <= cellParts.length &&
            parts.every((part, index) => partMatches(cellParts[offset + index], part, index === lastIndex)),
    );
};

/**
 * AB#37980: the date comparison for one filter box, in two rules.
 *
 * DOB cells are always zero-padded MM/DD/YYYY (the column renders toDisplayDob()'s output), which is
 * why digits typed into the filter matched nothing at all - the reported "10221996", against a cell
 * whose text carries slashes.
 *
 *  - SEPARATORS TYPED. They say where each part begins, so the parts are compared part-to-part
 *    against MM | DD | YYYY (alignedPartsMatch). Sliding the digits across a part boundary instead
 *    would make "10/22" match a patient born 01/02/2000 ("01022000" contains "1022").
 *  - NO SEPARATOR. Nothing says whether "121970" means 12/1970, 1/2/1970 or 12/19/70, so the run is
 *    NOT re-partitioned into a date: it is matched as a PREFIX of the cell's MMDDYYYY digits, which
 *    carries the bug's own "10221996" and every prefix of it as it is typed. A prefix and not a
 *    substring, because a substring slides the run across a part boundary: "1021996" (10/2/1996
 *    typed unpadded) sits at offset 1 of both 01021996 and 11021996, so it listed two patients born
 *    on OTHER dates while omitting 10/02/1996. So an unpadded part or a 2-digit year is honoured
 *    where a separator states it ("10/2/1996", "10/22/96"), while bare "1021996" and bare "102296"
 *    match nothing, neither being the start of any zero-padded MM/DD/YYYY.
 */
function dateFilterMatches(cellText: string, filter: string): boolean {
    const parts = dateParts(filter);
    if (parts.length === 0) {
        return false;   // no digits at all: an ACTIVE filter that matches nothing (see matchesFilter)
    }
    if (parts.length === 1) {
        return digitsOnly(cellText).startsWith(parts[0]);
    }
    return alignedPartsMatch(cellText, parts);
}

/**
 * A filter that names ONE whole date: three separator-delimited parts ending in a COMPLETE 4-digit
 * year. Nothing in such a value is still half-typed, so it is answered part-to-part and by nothing
 * else - see matchesFilter. A trailing part of 2 digits is deliberately not this: it reads as a
 * finished 2-digit year AND as the first half of a 4-digit one, and only the specialist knows which.
 */
const namesAWholeDate = (filter: string): boolean => {
    const parts = dateParts(filter);
    return parts.length === 3 && parts[2].length === 4;
};

/**
 * AB#37980: one filter box's predicate. Every column is a case-insensitive SUBSTRING test over the
 * rendered cell text, which is why "10221996" typed into the DOB filter used to match nothing - the
 * cell reads "10/22/1996".
 *
 * A column that declares `filterKind: "date"` answers dates instead, in three steps:
 *
 *  - A value that cannot be a date at all (a name pasted into the box) stays an ACTIVE filter that
 *    matches nothing, rather than an empty one that would silently re-list every candidate.
 *  - A WHOLE date ("1/12/1970") is answered part-to-part and by nothing else. The plain substring
 *    test is deliberately NOT consulted for it: "1/12/1970" is literally inside the rendered
 *    "11/12/1970", so it used to list a patient born in NOVEMBER as the only row, under a DOB the
 *    specialist typed to tell same-name candidates apart. Clicking it binds that patient to the
 *    request (PatientInfoRow -> onModalSelect), which is worse than the empty grid the bug reported.
 *  - Anything shorter is still being typed, or starts mid-date. It keeps the plain substring test
 *    the box has always had - "22/1996", "/25/1985" and "1/1" are legitimate and were working - and
 *    falls through to the date comparison only when that fails, so such a filter can only ever gain
 *    rows. That is why deleting a digit widens the grid again instead of re-partitioning what is
 *    left, and why a value whose year is unfinished can still list a near-miss DOB alongside the one
 *    being typed for.
 *
 * In every step the box itself is never rewritten, so no keystroke is swallowed and no value the
 * specialist did not type is ever displayed as though they had.
 *
 * Opt-in per column, so the requestor grid (ORGANIZATION / CONTACT / FAX / PRIOR) and every
 * non-date patient column are bit-for-bit unchanged.
 */
function matchesFilter(cellText: string, filter: string, isDateColumn: boolean): boolean {
    const needle = filter.trim().toLowerCase();
    if (!isDateColumn) {
        return cellText.toLowerCase().includes(needle);
    }
    if (!DATE_FILTER_CHARS.test(needle)) {
        return false;
    }
    if (namesAWholeDate(needle)) {
        return dateFilterMatches(cellText, needle);
    }
    return cellText.toLowerCase().includes(needle) || dateFilterMatches(cellText, needle);
}

/**
 * Filterable candidate GRID results modal (Spec #34101 (F1c), AC9) - realizes
 * the master-demo-v6 Patient/Requestor Search grid, superseding the earlier
 * minimal list modal. Reuses the app's classic grid pattern (the Material-UI
 * Table + the "second-row-below-column-names filter" the PO referenced, styled
 * with the same `table-filter-input` class as common/table/CustomTable) but is
 * generic over columns so both the patient (First/Last/DOB/EMPI) and requestor
 * (Organization/Contact/Fax/Prior) grids share one implementation.
 *
 * PHI note (hipaa-compliance.md): candidate values are patient/requestor PII -
 * this component never logs them.
 */
/**
 * Round-2 review Suggestion 1: a constant, not a per-instance-counter-generated
 * id. Two MatchGridModal instances can never be mounted simultaneously - each is
 * a `position: fixed; z-index: 10000` full-screen overlay, and the only would-be
 * collision partner (RequestorInfoRow's own local grid) is the dead, unreachable
 * path the spec documents in `## Out of Scope`. An earlier version hand-rolled a
 * `useId()`-style uniqueness guarantee (module-level counter + lazy `useState`
 * initializer) for that scenario, but nothing in the suite could tell a shared id
 * apart from a unique one - untested machinery justified by an unreachable case
 * is worse than the constant it replaced.
 */
const SUBTITLE_ID = "match-grid-modal__subtitle";

/**
 * Round-5 review: `MatchGridModal.tsx:331`'s `tabIndex={0}` (AB#38472, `66d1227`)
 * made candidate rows tab stops, and the spec's own rejection of `role="button"`
 * on a `<tr>` (correct - it must keep its implicit row semantics or the cells
 * lose their table association) means a screen-reader user who tabs onto a row
 * hears only the cell contents, with no indication Enter (or Space - the row's
 * own keydown handler below accepts both) selects it. One hidden hint,
 * referenced by every row's `aria-describedby`, closes that gap without
 * touching row semantics. Kept short on purpose - round 2 already established
 * that a long instruction repeated per row is noise, not help - but PR #10205
 * human review: the hint must name BOTH keys the handler actually accepts, so
 * it is six words rather than four; accuracy about which keys work outweighs
 * the two extra words.
 */
const ROW_HINT_ID = "match-grid-modal__row-hint";

const MatchGridModal: React.FC<MatchGridModalProps> = (props) => {
    const { ref: dialogRef, onKeyDown } = useModalFocus(props.onClose);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const firstSortable = props.columns.find(column => column.sortable);
    const [sortBy, setSortBy] = useState<string | undefined>(firstSortable?.id);
    const [sortDir, setSortDir] = useState<SortDirection>("asc");

    const onFilterChange = (event: ChangeEvent<HTMLInputElement>, columnId: string) => {
        const value = event.target.value;
        setFilters(prev => ({ ...prev, [columnId]: value }));
    };

    const onSort = (columnId: string) => {
        if (sortBy === columnId) {
            setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(columnId);
            setSortDir("asc");
        }
    };

    const visibleRows = useMemo(() => {
        const activeFilters = Object.entries(filters).filter(([, value]) => value.trim() !== "");
        const dateColumnIds = new Set(props.columns.filter(column => column.filterKind === "date").map(column => column.id));
        const filtered = props.rows.filter(row =>
            activeFilters.every(([columnId, value]) =>
                matchesFilter(row.values[columnId] ?? "", value, dateColumnIds.has(columnId)),
            ),
        );

        if (!sortBy) {
            return filtered;
        }
        const sorted = [...filtered].sort((a, b) =>
            (a.values[sortBy] ?? "").localeCompare(b.values[sortBy] ?? "", undefined, { numeric: true, sensitivity: "base" }),
        );
        return sortDir === "asc" ? sorted : sorted.reverse();
    }, [props.rows, props.columns, filters, sortBy, sortDir]);

    return (
        <>
            <div className="match-grid-modal__backdrop" onClick={props.onClose} />
            <div
                ref={dialogRef}
                className="match-grid-modal"
                role="dialog"
                aria-modal="true"
                aria-label={props.title}
                // Round-2 review Suggestion 2: on the DIALOG root, not the rows. The
                // subtitle ("Select a matching X... or click Create New...") is a
                // dialog-level instruction, announced once on open - pointing every
                // row at it (round-1's original placement) made a screen reader repeat
                // that instruction on each row traversed. Omitted, not pointed at a
                // nonexistent id, when there is no subtitle.
                aria-describedby={props.subtitle ? SUBTITLE_ID : undefined}
                tabIndex={-1}
                onKeyDown={onKeyDown}
            >
                <div className="match-grid-modal__header">
                    <div>
                        <h2 className="match-grid-modal__title">{props.title}</h2>
                        {props.subtitle ? (
                            <p id={SUBTITLE_ID} className="match-grid-modal__subtitle">{props.subtitle}</p>
                        ) : null}
                    </div>
                    <button type="button" className="match-grid-modal__close" aria-label="Close" onClick={props.onClose}>
                        &times;
                    </button>
                </div>

                <div className="match-grid-modal__table-wrap">
                    <Table size="small" stickyHeader className="match-grid-modal__table">
                        <TableHead>
                            <TableRow>
                                {props.columns.map(column => (
                                    <TableCell key={column.id} sortDirection={sortBy === column.id ? sortDir : false}>
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={sortBy === column.id}
                                                direction={sortBy === column.id ? sortDir : "asc"}
                                                onClick={() => onSort(column.id)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow className="match-grid-modal__filter-row">
                                {props.columns.map(column => (
                                    <TableCell key={`filter-${column.id}`}>
                                        <input
                                            type="text"
                                            className="table-filter-input match-grid-modal__filter-input"
                                            data-filter={column.id}
                                            aria-label={`Filter ${column.label}`}
                                            placeholder={column.filterKind === "date" ? DATE_FILTER_PLACEHOLDER : undefined}
                                            value={filters[column.id] ?? ""}
                                            onChange={event => onFilterChange(event, column.id)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.length === 0 ? (
                                <TableRow className="match-grid-modal__empty">
                                    <TableCell colSpan={props.columns.length}>No matching records found</TableCell>
                                </TableRow>
                            ) : (
                                visibleRows.map(row => (
                                    <TableRow
                                        key={row.id}
                                        className="match-grid-modal__row"
                                        data-row-id={row.id}
                                        tabIndex={0}
                                        aria-describedby={ROW_HINT_ID}
                                        onClick={() => props.onSelect(row.id)}
                                        onKeyDown={event => {
                                            // Round-1 review Warning 2: keydown bubbles up from any
                                            // descendant, and `adornments` (above) is a public
                                            // extension point that can inject arbitrary JSX into a
                                            // cell - a future focusable adornment (e.g. a real
                                            // <button>) must handle its OWN Enter/Space without also
                                            // triggering the row's onSelect. Only the row itself.
                                            if (event.target !== event.currentTarget) {
                                                return;
                                            }
                                            // AC9/Business Rule 8: Enter/Space invoke the same
                                            // onSelect the click handler does. Space is prevented
                                            // so it doesn't ALSO scroll the page.
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                props.onSelect(row.id);
                                            }
                                        }}
                                    >
                                        {props.columns.map(column => (
                                            <TableCell key={column.id}>
                                                {row.values[column.id]}
                                                {row.adornments?.[column.id]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/*
                 * Round-5 review: sibling of __table-wrap, deliberately OUTSIDE the
                 * <table> - a <span> inside <table>/<tbody> is invalid HTML and browsers
                 * hoist it out, which would break the id this relies on. `.sr-only` is
                 * Bootstrap's own utility, already available app-wide via the global
                 * `bootstrap/dist/css/bootstrap.css` import (src/index.tsx:8) - no new
                 * CSS, no new dependency.
                 */}
                <span id={ROW_HINT_ID} className="sr-only">Press Enter or Space to select</span>

                <button type="button" className="match-grid-modal__create-new" onClick={props.onCreateNew}>
                    {props.createNewLabel}
                </button>
            </div>
        </>
    );
};

export default MatchGridModal;
